import { useEffect } from 'react';

/**
 * Module-level "unsaved work" signal, used to make stale-chunk auto-reload
 * non-destructive. A page registers while it holds unsubmitted local state
 * (e.g. a half-filled check-in/QC form or staged-but-unposted photos). The
 * error boundaries consult {@link hasUnsavedWork} before reloading: if no work
 * is registered they behave exactly as before (reload); if work is registered
 * they skip the reload and render a manual-refresh fallback instead.
 *
 * Default is 0/false, so every surface that does NOT opt in behaves identically
 * to today.
 */
let dirtyCount = 0;

/** Register one unit of unsaved work. Returns a cleanup that releases it. */
export function markUnsavedWork(): () => void {
  dirtyCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    dirtyCount = Math.max(0, dirtyCount - 1);
  };
}

/** True when any mounted surface currently holds unsubmitted local state. */
export function hasUnsavedWork(): boolean {
  return dirtyCount > 0;
}

/**
 * Hook: while `isDirty` is true, this component counts as unsaved work. The
 * registration is released on unmount or when `isDirty` flips back to false.
 */
export function useUnsavedWork(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;
    const release = markUnsavedWork();
    return release;
  }, [isDirty]);
}
